import { busyTracker, UnauthorizedError } from '../../../shared/src';
import { api, type AuditExportFormat, type AuditExportScope, type AuditLogFilters } from '../api';
import { getOrgSlug } from '../utils/getOrgSlug';
import {
  createCsvWriter,
  filesFor,
  runExportPages,
  type ExportPosition,
  type ExportProgress,
  type ExportStoppedError,
} from './auditExport';
import { createXlsxWriter } from './xlsxWriter';

const PAGE_ATTEMPTS = 3;

/**
 * How long an export's pages can be fetched after it is created (the API's
 * EXPORT_TTL_MINUTES is 60): past it, continuing is pointless.
 */
const CONTINUE_WINDOW_MS = 55 * 60 * 1000;

/** An export underway: where it stands, and what its files are called. */
export interface AuditExportRun extends ExportPosition {
  baseName: string;
  createdAt: number;
}

interface RunCallbacks {
  timeZone: string;
  onProgress: (progress: ExportProgress) => void;
  isCancelled: () => boolean;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Export Admin > Audit Logs to files the browser saves. Creating the export
 * records it in the log (whatever happens next: the rows are about to leave),
 * then its pages are fetched oldest first, each retried on a transient error,
 * and saved as files of up to EVENTS_PER_FILE events. Brackets itself with
 * busyTracker so a deploy's auto-refresh waits for it, and in-app navigation
 * asks first. A stop throws ExportStoppedError<AuditExportRun>, whose
 * resumeAt continueAuditExport takes.
 */
export async function runAuditExport(
  options: RunCallbacks & { format: AuditExportFormat; scope: AuditExportScope; filters: AuditLogFilters }
): Promise<void> {
  busyTracker.begin();
  try {
    const { export: created } = await api.createAuditLogExport({
      format: options.format,
      scope: options.scope,
      filters: options.filters,
    });
    const run: AuditExportRun = {
      exportId: created.id,
      format: options.format,
      total: created.total_rows,
      // The export's own row rides in the last file
      files: filesFor(created.total_rows - 1),
      file: 1,
      fetched: 0,
      baseName: `audit-logs-${getOrgSlug()}-${fileTimestamp()}`,
      createdAt: Date.now(),
    };
    await exportFrom(run, options);
  } finally {
    busyTracker.end();
  }
}

/** Continue a stopped export from its next file, under the same audit record. */
export async function continueAuditExport(run: AuditExportRun, options: RunCallbacks): Promise<void> {
  busyTracker.begin();
  try {
    await exportFrom(run, options);
  } finally {
    busyTracker.end();
  }
}

/** Whether a stopped export can still be continued (its pages are served for an hour). */
export function canContinue(run: AuditExportRun): boolean {
  return Date.now() - run.createdAt < CONTINUE_WINDOW_MS;
}

function exportFrom(run: AuditExportRun, { timeZone, onProgress, isCancelled }: RunCallbacks): Promise<void> {
  return runExportPages({
    position: run,
    fetchPage: (cursor) => fetchPage(run.exportId, cursor),
    createWriter: async () => (run.format === 'csv' ? createCsvWriter(timeZone) : createXlsxWriter(timeZone)),
    saveFile: (blob, file, files) =>
      saveBlob(blob, `${run.baseName}${files > 1 ? `-part-${file}-of-${files}` : ''}.${run.format}`),
    onProgress,
    isCancelled,
  });
}

export type AuditExportStopped = ExportStoppedError<AuditExportRun>;

async function fetchPage(exportId: string, cursor: string | undefined) {
  for (let attempt = 1; ; attempt++) {
    try {
      return await api.getAuditLogExportRows(exportId, cursor);
    } catch (err) {
      // A dead session will not come back by retrying: the client already signs out
      if (err instanceof UnauthorizedError || attempt >= PAGE_ATTEMPTS) throw err;
      await sleep(500 * 2 ** (attempt - 1));
    }
  }
}

/** Local `YYYY-MM-DD-HHmm` for the file name. */
function fileTimestamp(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
}

function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking right away can cancel the download in some browsers
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
